<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import Button from '@/components/ui/Button.vue';
import SearchInput from '@/components/ui/SearchInput.vue';
import TabsSwitcher from '@/components/ui/TabsSwitcher.vue';
import MainLayout from '@/layouts/MainLayout.vue';
import { useThreadStore } from '@/stores/thread';
import type { HomeTabs } from '@/types/thread';
import ThreadsList from '@/views/home/components/ThreadsList.vue';
import ThreadsModeSwitcher from '@/views/home/components/ThreadsModeSwitcher.vue';

const { t } = useI18n();
const router = useRouter();
const queryParams = router.currentRoute.value.query;

const threadStore = useThreadStore();
const { selectedMode, selectedTab, loading } = toRefs(threadStore);
const threadList = ref();

const searchParams = computed(() => {
  const params: { creator?: { uuid: string; name: string } } = {};
  if (queryParams?.creator_uuid) {
    params.creator = {
      uuid: queryParams.creator_uuid as string,
      name: queryParams.creator_name as string || ''
    };
  }

  return params;
});

const changeSelectedTab = async (tab: HomeTabs) => {
  if (loading.value) return;
  await threadList.value.refresh({ tab });
};

const searchInputChanged = async (searchParams) => {
  if (queryParams?.mode) {
    const mode = queryParams?.mode === 'company' ? 'company' : 'personal';
    selectedMode.value = { label: t(`home.modes.${mode}`), value: `${mode}` };
  }
  await threadList.value.refresh({ searchParams });
};
</script>

<template>
  <MainLayout home>
    <div class="filters flex-center flex-center--space-between">
      <ThreadsModeSwitcher />

      <TabsSwitcher
        :tabs="['all', 'sent', 'trash']"
        :selected-tab="selectedTab"
        :loading="loading"
        @onChange="changeSelectedTab"
      />

      <SearchInput
        :placeholder="$t('placeholders.searchMessages')"
        :params="searchParams"
        @changed="searchInputChanged"
      />

      <router-link to="/new-message">
        <Button>{{ $t('buttons.newMessage') }}</Button>
      </router-link>
    </div>

    <ThreadsList ref="threadList" />
  </MainLayout>
</template>

<style lang="scss">
.filters {
  margin-bottom: 36px;
  width: 100%;
  padding: 0 30px;
}
</style>
